import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import { Box, IconButton, ListItem, ListItemButton, Link as MUILink, SwipeableDrawer, Typography, css, styled } from '@mui/material'
import Logo from 'assets/icons/ClientLogo.svg'
import LogoWhite from 'assets/icons/ClientLogoWhite.svg'
import { ReactComponent as UsageIcon } from 'assets/icons/UsageIcon.svg'
import { MButton } from 'components/ui'
import { OUTAGES_LINK, CONTACT_US_LINK, ROUTES_PATH, SIDEBAR_ITEMS } from 'config'
import { color } from 'config/color'
import { useAuth, useSidebar } from 'context'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import { ServiceAddress } from './ServiceAddress'

export const SideBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { openSidebar, setOpenSidebar } = useSidebar()
  const { handleLogOut } = useAuth()
  const isMobile = useMobileBreakpoints()

  const handleLogout = async (): Promise<void> => {
    await handleLogOut()
    navigate(ROUTES_PATH.SIGNIN)
  }

  const handleListItemClick = (href: string): void => {
    if (href === OUTAGES_LINK || href === CONTACT_US_LINK) {
      window.open(href, '_blank')
    } else {
      navigate(href)
      setOpenSidebar(false)
    }
  }

  const handleListItemKeyDown = (href: string) => (e: React.KeyboardEvent): void => {
    if (e.key !== ' ' && e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    handleListItemClick(href)
  }

  const handleNavItemKeyDown = (href: string) => (e: React.KeyboardEvent<HTMLAnchorElement>): void => {
    if (e.key !== ' ' && e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    handleListItemClick(href)
  }

  const handleLogoLinkKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>): void => {
    if (e.key !== ' ' && e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    navigate(ROUTES_PATH.DASHBOARD)
  }

  return (
    <>
      {isMobile
        ? <SwipeableDrawer
          PaperProps={{
            sx: { borderTopLeftRadius: 10, borderBottomLeftRadius: 10 },
            role: 'navigation',
            'aria-label': 'Main navigation'
          }}
          anchor="right"
          open={openSidebar}
          onClose={() => { setOpenSidebar(false) }}
          onOpen={() => { setOpenSidebar(true) }}
          id="sidebar-navigation"
        >
          <Box p={3} width="70vw" height="100%" display="flex" flexDirection="column" justifyContent="space-between">
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography color={color.black} fontWeight={600} fontSize={24} >More Options</Typography>
                <IconButton onClick={() => { setOpenSidebar(false) }} aria-label="close navigation menu"><CloseIcon color='primary' /></IconButton>
              </Box>
              {SIDEBAR_ITEMS.map((item, index) => (
                <ListItem key={index} sx={{ my: 1.5 }} disablePadding>
                  <ListItemButton
                    disableGutters
                    onClick={() => { handleListItemClick(item.href) }}
                    onKeyDown={handleListItemKeyDown(item.href)}
                    aria-current={item.href === location.pathname ? 'page' : undefined}
                  >
                    {(item.icon != null) ? <item.icon sx={{ color: item.href === location.pathname ? color.primary[600] : color.primary[700] }} /> : <UsageIcon fill={item.href === location.pathname ? color.primary[600] : color.primary[700]} />}
                    <Typography ml={2} fontSize={16} color={color.grey[900]} >{item.title}</Typography>
                  </ListItemButton>
                </ListItem>
              ))}
            </Box>
            <Box py={3} borderTop={`solid 1px ${color.white}`} display="flex" justifyContent="space-between" alignItems="center">
              <ListItemButton disableGutters onClick={handleLogout}>
                <LogoutIcon sx={{ color: color.primary[700], mr: 2 }} />
                <Typography fontSize={16} color={color.grey[900]} >Log out</Typography>
              </ListItemButton>
              <Box component="img" src={Logo} height={30} alt="background" />
            </Box>
          </Box>
        </SwipeableDrawer >
        : <NavigationWrapper role="navigation" aria-label="Main navigation">
          <SidebarInner>
            <Box display="block" textAlign="center" my={2}>
              <MUILink
                component="button"
                type="button"
                aria-label="Go to dashboard"
                onClick={() => { navigate(ROUTES_PATH.DASHBOARD) }}
                onKeyDown={handleLogoLinkKeyDown}
                sx={{
                  p: 0,
                  m: 0,
                  border: 'none',
                  background: 'none',
                  textDecoration: 'none',
                  display: 'inline-block',
                  lineHeight: 0,
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: `2px solid ${color.white}`,
                    outlineOffset: 2,
                    borderRadius: 1
                  }
                }}
              >
                <img src={LogoWhite} height={50} alt="Delco Water Company Homepage" />
              </MUILink>
            </Box>
            <Box
              my={5}
              mx={2}
              display="flex"
              alignItems="center"
              flexDirection="column">
              <Box bgcolor={color.primary[800]} width="93%" display="flex" p={1} alignItems="center" justifyContent="center">
                <Typography fontSize="12px" color={color.white} letterSpacing={1}>
                  SERVICE ADDRESS
                </Typography>
              </Box>
              <ServiceAddress />
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="space-between" height="calc(100vh - 300px)">
              <Box display="flex" flexDirection="column" gap={1} mx={2} ml={2} mr={0.5}
                sx={{
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 3
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: color.background[900]
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: color.primary[600],
                    borderRadius: 1
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: color.primary[600]
                  }
                }}>
                {SIDEBAR_ITEMS.map((link, index) => (
                  <NavItem key={index}
                    to={link.href}
                    {...((link.href === OUTAGES_LINK || link.href === CONTACT_US_LINK) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    isactive={link.href === location.pathname ? 'true' : 'false'}
                    onKeyDown={handleNavItemKeyDown(link.href)}
                    aria-current={link.href === location.pathname ? 'page' : undefined}
                  >
                    {(link.icon != null) ? <link.icon sx={{ color: link.href === location.pathname ? color.white : color.background[600] }} /> : <UsageIcon fill={link.href === location.pathname ? color.white : color.background[600]} />}
                    <span>{link.title}</span>
                  </NavItem>
                ))}
              </Box>
              <Box paddingLeft="30px" paddingRight="30px" marginBottom="30px">
                <MButton
                  fullWidth
                  variant="outlined"
                  type="button"
                  rounded="true"
                  onClick={handleLogout}
                  sx={{ fontSize: 14, color: color.white, borderColor: color.background[600] }}
                  texttransform="none"
                  startIcon={<LogoutIcon />}
                >
                  Log out
                </MButton>
              </Box>
            </Box>
          </SidebarInner>
        </NavigationWrapper >
      }
    </>
  )
}

const NavigationWrapper = styled('nav')({
  backgroundColor: color.background[900],
  height: '100vh',
  left: 0,
  top: 0,
  position: 'fixed',
  width: '240px',
  zIndex: 999,
  transition: 'all 0.25s'
})
const SidebarInner = styled(Box)`
  padding-top: 56px;
  overflow: hidden;
    height: auto;
`

const NavItem = styled(Link) <{ isactive: string }>`
  color: ${color.white};
  display: flex;
  align-items: center;
  padding: 12px 39px 11px 18px;
  position: relative;
  text-decoration: none;
  cursor: pointer;
  border-left: solid 5px ${color.background[900]};
  outline: none;

  &:focus-visible {
    outline: 2px solid ${color.white};
    outline-offset: -2px;
    background-color: ${color.primary[900]};
    border-left: solid 5px ${color.primary[800]};
    border-radius: 4px;
    margin-right: 15px;
  }

  ${(props) => css`
    ${props.isactive === 'true' &&
    css`
      color: ${color.white};
      background-color: ${color.primary[900]} ;
      border-left: solid 5px ${color.primary[800]};
      border-radius: 4px;
      margin-right: 15px;

      
    `}
  `}

  span {
    font-size: 14px;
    line-height: 17px;
    padding: 0 0 0 8px;
    position: relative;
  }

  img: {
    position: absolute;
    height: 20px;
    width: 20px;
  }
`
